import React, { useEffect, useMemo, useState } from 'react';
import { axisName, marketImageUrl, optionLabel } from '../../lib/marketCatalog';
import { arabicRest, dimsText, isUkPlug, parseLabel, splitAxis, UK_PLUG_AR } from '../../lib/variantSplit';

// The variant picker (owner, 2026-11-02 — web clarity redesign, part 4).
// Per supplier axis, one of three ways:
//   split  — a combined axis shown as its real attributes (اللون / المقاس /
//            النوع), each a row of chips; colour and picture values as image chips;
//   folded — one button with the chosen variant in short form and how many
//            there are, opening a bottom sheet: search, quick filters, every
//            variant with its delivered price, «تم»;
//   plain  — a few short values as chips.
// The page's state stays the ORIGINAL axis value per axis (`picked`), so a
// selection always resolves through skuFor() to one real supplier SKU — the
// SKU the cart and the automated purchase use. A chip whose combination does
// not exist or is out of stock is disabled.

const T = {
  ar: { pick: 'اختر الخيار', options: (n) => `${n} خيار متاح`, search: 'ابحث: أبيض، 100 سم، علوي...', done: 'تم', none: 'لا يوجد خيار بهذا الوصف', dims: 'الأبعاد', choose: 'اختر', unavailable: 'غير متاح مع اختيارك', all: 'الكل', close: 'إغلاق' },
  en: { pick: 'Choose an option', options: (n) => `${n} options available`, search: 'Search: white, 100 cm, top…', done: 'Done', none: 'No option matches', dims: 'Dimensions', choose: 'Choose', unavailable: 'Not available with your choice', all: 'All', close: 'Close' },
};
T.zh = T.en;

/** Per axis: the stored split when it still fits the listing, else the rules. */
export function axisModes(axes, stored) {
  return (axes || []).map((ax, a) => {
    const s = stored?.axes?.[a];
    const fits = s && (s.mode !== 'split' || (Array.isArray(s.values) && s.values.length === (ax.values || []).length));
    return fits ? s : splitAxis(ax);
  });
}

/** A value's label, with the British plug named for Saudi Arabia (owner, 2026-11-02). */
export function valueLabel(v, lang = 'ar', compact = false) {
  if (isUkPlug(v)) return compact ? (lang === 'ar' ? 'بريطاني' : 'British') : lang === 'ar' ? UK_PLUG_AR : 'British (used in Saudi Arabia)';
  return optionLabel(v, lang);
}

/** «80 × 50 × 185 سم» — numbers left to right, isolated from the Arabic around it (owner, 2026-11-02). */
export function Dims({ d, lang = 'ar' }) {
  if (!d) return null;
  return <bdi dir="ltr" className="mk-dims-val">{dimsText(d)}</bdi>;
}

/** «أسود · 80 سم · تعليق مزدوج» — a variant's short form. */
// compact: «بريطاني» alone (the sticky bar, where the long label is cut)
export function shortLabel(v, lang = 'ar', compact = false) {
  if (!v) return '';
  if (isUkPlug(v)) { const col = parseLabel(v.label_en || '').color; return [lang === 'ar' ? col?.ar : col?.key, valueLabel(v, lang, compact)].filter(Boolean).join(' · '); }
  if (lang !== 'ar') return optionLabel(v, lang).replace(/,?\s*(?:l(?:ength)?\s*)?\d+(?:\.\d+)?\s*[*x×].*$/i, '').trim();
  const p = parseLabel(v.label_en || '');
  // only when a colour or a measured size was really found — «تصميم 1» stays as it is
  if (!p.color && !(p.size && p.size.unit)) return optionLabel(v, lang).replace(/،?\s*طول\s*\d.*$/, '').trim();
  const rest = arabicRest(v.label_ar);
  const parts = [p.color?.ar, p.size?.ar, rest].filter(Boolean);
  return parts.length >= 2 ? parts.join(' · ') : optionLabel(v, lang);
}

function Chip({ on, enabled, image, label, onClick, data }) {
  return (
    <button type="button" role="radio" aria-checked={on} disabled={!enabled && !on} title={label} onClick={onClick}
      className={`mk-opt${on ? ' on' : ''}${enabled || on ? '' : ' off'}${image ? ' img' : ''}`} {...data}>
      {image && <img src={marketImageUrl(image, true)} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
      <span>{label}</span>
    </button>
  );
}

/** Value indexes with the British plug first (owner, 2026-11-02); the rest keep their order. */
export function ukFirst(values, idx = values.map((_, j) => j)) {
  return [...idx.filter((j) => isUkPlug(values[j])), ...idx.filter((j) => !isUkPlug(values[j]))];
}

// ── split ────────────────────────────────────────────────────────────────────
function SplitAxis({ axis, split, a, chosen, avail, onPick, lang, font }) {
  const c = T[lang] || T.ar;
  const cur = chosen != null ? split.values[chosen] : null;
  const curAttrs = cur ? cur.attrs : split.attrs.map(() => null);
  const alone = cur && split.attrs.some((at, k) => at.key === 'rest' && at.values[curAttrs[k]]?.alone);
  // the original value a chip leads to: same other attributes where they apply, available
  const target = (k, m) => {
    const ok = (i) => avail?.[i] && split.values[i].attrs[k] === m;
    const exact = split.values.findIndex((v, i) => ok(i) && v.attrs.every((x, l) => l === k || x == null || curAttrs[l] == null || x === curAttrs[l]));
    if (exact >= 0) return exact;
    return alone || curAttrs.every((x, l) => l === k || x == null) ? split.values.findIndex((v, i) => ok(i)) : -1;
  };
  const dims = cur?.dims || split.dims;
  return (
    <div className="mk-axis" data-axis-box={a} data-axis-mode="split">
      {split.attrs.map((at, k) => {
        if (alone && at.key !== 'rest') return null;
        return (
          <div key={at.key} className="mk-subaxis" data-subaxis={at.key}>
            <p className="mk-axis-head" style={{ fontFamily: font }}>
              <span className="mk-axis-name">{lang === 'ar' ? at.name_ar : at.name_en}</span>
              {curAttrs[k] != null && <span className="mk-axis-chosen" data-axis-chosen={`${a}.${k}`}>{lang === 'ar' ? at.values[curAttrs[k]]?.ar : at.values[curAttrs[k]]?.en}</span>}
            </p>
            <div className="mk-axis-values" role="radiogroup" aria-label={lang === 'ar' ? at.name_ar : at.name_en}>
              {at.values.map((v, m) => {
                const to = target(k, m);
                return (
                  <Chip key={v.key} on={curAttrs[k] === m} enabled={to >= 0} image={v.image_ref} label={lang === 'ar' ? v.ar : v.en}
                    data={{ 'data-axis': a, 'data-subvalue': `${k}.${m}` }} onClick={() => to >= 0 && onPick(to)} />
                );
              })}
            </div>
          </div>
        );
      })}
      {dims && <p className="mk-dims" data-dims="1">{c.dims}: <Dims d={dims} /></p>}
    </div>
  );
}

// ── folded ───────────────────────────────────────────────────────────────────
export function filterFolded(values, needle, quick, lang = 'ar') {
  const words = String(needle || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  return values.map((_, j) => j).filter((j) => {
    const v = values[j];
    const hay = `${v.label_ar || ''} ${v.label_en || ''} ${shortLabel(v, lang)}`.toLowerCase();
    if (quick && !hay.includes(quick.toLowerCase())) return false;
    return words.every((w) => hay.includes(w));
  });
}

function FoldedAxis({ axis, a, chosen, avail, onPick, lang, font, priceFor }) {
  const c = T[lang] || T.ar;
  const values = useMemo(() => axis.values || [], [axis]);
  const [open, setOpen] = useState(false);
  const [needle, setNeedle] = useState('');
  const [quick, setQuick] = useState('');
  const [pending, setPending] = useState(chosen);
  useEffect(() => { if (open) setPending(chosen); }, [open, chosen]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', esc);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', esc); document.body.style.overflow = prev; };
  }, [open]);
  const availableN = values.filter((_, j) => avail?.[j]).length;
  // quick filters: the colours and sizes the labels carry (two or more of a kind)
  const quicks = useMemo(() => {
    const col = new Map(); const siz = new Map();
    values.forEach((v) => { const p = parseLabel(v.label_en || ''); if (p.color) col.set(p.color.ar, p.color.key); if (p.size) siz.set(p.size.ar, p.size.key); });
    const pick = (m) => (m.size >= 2 && m.size <= 10 ? [...m.keys()] : []);
    return [...pick(col), ...pick(siz)];
  }, [values]);
  const shown = ukFirst(values, filterFolded(values, needle, quick, lang));
  const cur = chosen != null ? values[chosen] : null;
  // the supplier's axis name only when the list is purely colours; «اختر الخيار» otherwise (owner, 2026-11-02)
  const pureColors = values.every((v) => { const p = parseLabel(v.label_en || ''); return p.color && !p.size && !p.dims && !p.rest; });
  const name = (pureColors && axisName(axis, lang)) || c.pick;
  return (
    <div className="mk-axis" data-axis-box={a} data-axis-mode="folded">
      <p className="mk-axis-head" style={{ fontFamily: font }}><span className="mk-axis-name">{name}</span></p>
      <button type="button" className="mk-fold-btn" data-fold-open={a} onClick={() => setOpen(true)} aria-haspopup="dialog" style={{ fontFamily: font }}>
        {cur?.image_ref && <img src={marketImageUrl(cur.image_ref, true)} alt="" />}
        <span className="mk-fold-cur"><b data-axis-chosen={a}>{cur ? shortLabel(cur, lang) : c.choose}</b><small>{c.options(availableN)}</small></span>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {cur && parseLabel(cur.label_en || '').dims && <p className="mk-dims" data-dims="1">{c.dims}: <Dims d={parseLabel(cur.label_en || '').dims} /></p>}
      {open && (
        <div className="mk-sheet-back" data-fold-sheet={a} onClick={() => setOpen(false)}>
          <div className="mk-sheet" role="dialog" aria-modal="true" aria-label={name} onClick={(e) => e.stopPropagation()} style={{ fontFamily: font }}>
            <div className="mk-sheet-head">
              <b>{name}</b>
              <button type="button" className="mk-sheet-x" aria-label={c.close} onClick={() => setOpen(false)}>×</button>
            </div>
            <input className="mk-sheet-search" type="search" value={needle} placeholder={c.search} aria-label={c.search} onChange={(e) => setNeedle(e.target.value)} data-fold-search={a} />
            {quicks.length > 0 && (
              <div className="mk-sheet-quick" role="group">
                <button type="button" className={`mk-opt${!quick ? ' on' : ''}`} onClick={() => setQuick('')}>{c.all}</button>
                {quicks.map((q) => <button key={q} type="button" className={`mk-opt${quick === q ? ' on' : ''}`} data-fold-quick={q} onClick={() => setQuick(quick === q ? '' : q)}>{q}</button>)}
              </div>
            )}
            <div className="mk-sheet-list" role="radiogroup" aria-label={name}>
              {shown.map((j) => {
                const v = values[j]; const ok = !!avail?.[j]; const on = pending === j;
                const d = parseLabel(v.label_en || '').dims; const price = ok ? priceFor?.(a, j) : null;
                return (
                  <button key={j} type="button" role="radio" aria-checked={on} disabled={!ok} data-fold-row={j}
                    className={`mk-sheet-row${on ? ' on' : ''}${ok ? '' : ' off'}`} onClick={() => setPending(j)} title={ok ? optionLabel(v, lang) : `${optionLabel(v, lang)} — ${c.unavailable}`}>
                    {v.image_ref ? <img src={marketImageUrl(v.image_ref, true)} alt="" loading="lazy" /> : <span className="mk-sheet-noimg" />}
                    <span className="mk-sheet-txt"><b>{shortLabel(v, lang)}</b>{d && <small><Dims d={d} /></small>}</span>
                    <span className="mk-sheet-price">{price || ''}</span>
                    <span className="mk-sheet-check" aria-hidden="true">{on ? '✓' : ''}</span>
                  </button>
                );
              })}
              {shown.length === 0 && <p className="mk-axis-none">{c.none}</p>}
            </div>
            <button type="button" className="mk-btn mk-sheet-done" data-fold-done={a} disabled={pending == null || !avail?.[pending]}
              onClick={() => { if (pending != null && pending !== chosen) onPick(pending); setOpen(false); }}>{c.done}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── plain ────────────────────────────────────────────────────────────────────
function PlainAxis({ axis, a, chosen, avail, onPick, lang, font }) {
  const values = axis.values || [];
  const name = axisName(axis, lang);
  return (
    <div className="mk-axis" data-axis-box={a} data-axis-mode="plain">
      <p className="mk-axis-head" style={{ fontFamily: font }}>
        <span className="mk-axis-name">{name}</span>
        {chosen != null && <span className="mk-axis-chosen" data-axis-chosen={a}>{valueLabel(values[chosen], lang)}</span>}
      </p>
      <div className="mk-axis-values" role="radiogroup" aria-label={name}>
        {ukFirst(values).map((j) => { const v = values[j]; return <Chip key={j} on={chosen === j} enabled={!!avail?.[j]} image={v.image_ref} label={valueLabel(v, lang)}
          data={{ 'data-axis': a, 'data-value': j }} onClick={() => onPick(j)} />; })}
      </div>
    </div>
  );
}

/**
 * axes: product.options.axes · modes: axisModes() · picked: chosen value per axis ·
 * avail: optionAvailability() · onPick(a, j) · priceFor(a, j) → «245 ر.س» for the sheet rows.
 */
export default function VariantPicker({ axes, modes, picked, avail, onPick, lang = 'ar', font, priceFor }) {
  return (
    <div className="mk-variants" data-growth-block="variants">
      {(axes || []).map((ax, a) => {
        const m = modes?.[a] || { mode: 'plain' };
        const props = { axis: ax, a, chosen: picked[a], avail: avail?.[a], onPick: (j) => onPick(a, j), lang, font };
        if (m.mode === 'split') return <SplitAxis key={a} split={m} {...props} />;
        if (m.mode === 'folded') return <FoldedAxis key={a} priceFor={priceFor} {...props} />;
        return <PlainAxis key={a} {...props} />;
      })}
    </div>
  );
}
